import React from "react";
import { useNavigate } from "react-router-dom";

const Button = ({
  children,
  variant = "primary",
  size = "md",
  icon,
  imgIcon,
  iconPosition = "left",
  href,
  className = "",
  disabled = false,
  loading = false,
  onClick,
  type = "button",
  ...props
}) => {
  const baseClasses =
    "inline-flex items-center justify-center font-semibold rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ";

  const variants = {
    primary: "bg-bluePrimary text-white hover:bg-blue-700 focus:ring-blue-500",
    secondary:
      "bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-500",
    success: "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500",
    danger: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500",
    warning:
      "bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-yellow-500",
    info: "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500",
    light: "bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500",
    dark: "bg-gray-800 text-white hover:bg-gray-900 focus:ring-gray-500", 
    orange:
      "bg-orange-600 text-white hover:bg-orange-700 focus:ring-orange-500",
    indigo:
      "bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500",
    outline:
      "border border-bluePrimary text-bluePrimary bg-bluePrimary/5 hover:bg-bluePrimary hover:text-white focus:ring-blue-500",
    "outline-success":
      "border border-green-500 text-green-500 bg-green-50 hover:bg-green-500 hover:text-white focus:ring-green-500",
    "outline-danger":
      "border border-red-500 text-red-500 bg-red-50 hover:bg-red-500 hover:text-white focus:ring-red-500",
    "outline-warning":
      "border border-yellow-500 text-yellow-500 bg-yellow-50 hover:bg-yellow-500 hover:text-white focus:ring-yellow-500",
    "outline-info":
      "border border-blue-500 text-blue-500 bg-blue-50 hover:bg-blue-500 hover:text-white focus:ring-blue-500",
    "outline-orange":
      "border border-orange-500 text-orange-500 bg-orange-50 hover:bg-orange-500 hover:text-white focus:ring-orange-500",
    "outline-purple":
      "border border-violet-500 text-violet-500 bg-violet-50 hover:bg-violet-500 hover:text-white focus:ring-violet-500",
    "outline-light":
      "border border-gray-300 text-gray-700 bg-gray-50 hover:bg-gray-100 focus:ring-gray-500",
    "outline-dark":
      "border border-gray-800 text-gray-800 bg-gray-50 hover:bg-gray-800 hover:text-white focus:ring-gray-500",
    "light-success":
      "bg-green-100 text-green-600 hover:bg-green-200 focus:ring-green-500",
    "light-danger":
      "bg-red-100 text-red-600 hover:bg-red-200 focus:ring-red-500",
    "light-warning":
      "bg-yellow-100 text-yellow-600 hover:bg-yellow-200 focus:ring-yellow-500",
    "light-info":
      "bg-blue-100 text-blue-600 hover:bg-blue-200 focus:ring-blue-500",
    "light-orange":
      "bg-orange-100 text-orange-600 hover:bg-orange-200 focus:ring-orange-500",
    "light-purple":
      "bg-purple-100 text-purple-600 hover:bg-purple-200 focus:ring-purple-500",
  };

  const sizes = {
    xs: "px-2 py-1 text-xs gap-1",
    sm: "px-3 py-2 text-sm gap-1.5",
    md: "px-4 py-2 text-sm gap-2",
    lg: "px-6 py-3 text-base gap-2",
    xl: "px-8 py-4 text-lg gap-2.5",
    icon: "p-2 gap-0",
  };

  const iconSizes = {
    xs: "h-3 w-3",
    sm: "h-4 w-4",
    md: "h-4 w-4",
    lg: "h-5 w-5",
    xl: "h-6 w-6",
    icon: "h-4 w-4",
  };

  const classes = [
    baseClasses,
    variants[variant] || variants.primary,
    sizes[size] || sizes.md,
    className,
  ].join(" ");

  const iconClasses = iconSizes[size] || iconSizes.md;

  const navigate = useNavigate();

  const renderIcon = () => {
    if (loading) {
      return (
        <svg
          className={`${iconClasses} animate-spin`}
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      );
    }

    // If imgIcon is provided, render it as an image
    if (imgIcon) {
      return <img src={imgIcon} alt="" className={iconClasses} />;
    }

    // If icon is provided, handle it as before
    if (icon) {
      // Check if icon is a React component (function/class) - Heroicons
      if (typeof icon === "function") {
        const IconComponent = icon;
        return <IconComponent className={iconClasses} />;
      }

      // If icon is already a JSX element (like img tag), return it as is
      if (React.isValidElement(icon)) {
        return icon;
      }

      // If icon is a string (like a URL), create an img element
      if (typeof icon === "string") {
        return <img src={icon} alt="" className={iconClasses} />;
      }
    }

    return null;
  };

  const renderContent = () => {
    if (loading) {
      return (
        <>
          {renderIcon()}
          <span>Loading...</span>
        </>
      );
    }

    // If size is "icon", only render the icon, no text
    if (size === "icon") {
      return renderIcon();
    }

    if (iconPosition === "right") {
      return (
        <>
          <span>{children}</span>
          {renderIcon()}
        </>
      );
    }

    return (
      <>
        {renderIcon()}
        <span>{children}</span>
      </>
    );
  };

  // Otherwise render as a button
  return (
    <button
      type={type}
      className={classes}
      disabled={disabled || loading}
      onClick={href ? () => navigate(href) : onClick}
      {...props}
    >
      {renderContent()}
    </button>
  );
};

export default Button;
